import * as React from 'react';
import { useEffect } from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import {
  LocalizationProvider,
  loadMessages,
  IntlProvider,
} from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import esMessages from '../../utils/gridMessageKey.json';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridCustomFilterCellProps,
} from '@progress/kendo-react-grid';
import { DataResult, State, process } from '@progress/kendo-data-query';
import {
  setExpandedState,
  setGroupIds,
} from '@progress/kendo-react-data-tools';
import { getLocalDateTime } from '../../utils/utils';
import {
  clearCommandsByBatchID,
  getCommandByBatchID,
  getDateCreatedCommandByBatchList,
  getAllConfigCommands,
  getConfigCommandByBatchID,
  getAllNBCCommands,
  getNBCCommandByBatchID,
} from '../../store/Commands/CommandsAction';
import { CustomDateFilterCell } from '../CustomDateFilterCell';
import StringResourceManager from '../StringResourceManager';
import { GroupedCommandRecord } from '../../Interfaces/Commands/CommandRecordInfo';
import CommandHistoryDialog from '../CommandsHistory/CommandHistoryDialog';
loadMessages(esMessages, 'fr-CA');
import { Button } from '@progress/kendo-react-buttons';
import { GetString } from '../StringResourceManager';
import { Checkbox, FormControlLabel } from '@mui/material';
import { Error } from '@progress/kendo-react-labels';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { arrowRotateCwIcon } from '@progress/kendo-svg-icons';
import { LoadingPanel } from '../LoadingPanel';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    logsHistoryContainer: {
      display: 'flex',
    },
    margin: {
      margin: theme.spacing(1),
    },
    gridStyle: {
      height: 400,
      marginLeft: '10px',
      marginBottom: '10px',
    },
    commandHistoryContainer: {
      marginLeft: '10px',
      marginBottom: '20px',
    },
    startDateRangeContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      width: 700,
      marginTop: '10px',
    },
    endDateRangeContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      width: 700,
      marginTop: '10px',
    },
    labelContainer: {
      width: 120,
    },
    dateTimePickerContainer: {
      display: 'flex',
      flexDirection: 'row',
    },
    divider: { border: 'solid 1px' },
    button: { marginLeft: '10px' },
    startDateTimePickerContainer: { width: 520 },
    endDateTimePickerContainer: { width: 520 },
  }),
);

interface Props {
  title: string;
  commandName: string;
  onStartDateChange?: (startDate: Date) => void;
  onEndDateChange?: (endDate: Date) => void;
  onUseCurrentDateTimeFilterChange?: (useCurrentDate: boolean) => void;
  dropDownFilterCell:
    | React.ComponentType<GridCustomFilterCellProps>
    | undefined;
}

const CreatedOnCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  return <td>{getLocalDateTime(dataItem.CreatedOn)}</td>;
};

const CommandHistory: React.FC<Props> = ({
  title,
  commandName,
  onStartDateChange,
  onEndDateChange,
  onUseCurrentDateTimeFilterChange,
  dropDownFilterCell: dropDownFilterCell,
}): React.ReactElement => {
  const classes = useStyles();

  const dispatch: Dispatch<any> = useDispatch();

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const initialDataState: State = {
    take: 50,
    skip: 0,
    sort: [{ field: 'CreatedOn', dir: 'desc' }],
    group: [],
  };

  const [dataState, setDataState] = React.useState<State>(initialDataState);

  const [collapsedState, setCollapsedState] = React.useState<string[]>([]);

  const [commandHistoryVisible, setCommandHistoryVisible] =
    React.useState<boolean>(false);

  const commandList: GroupedCommandRecord[] = useSelector(
    (state: ApplicationState) => state.commands.commandList,
  );

  const loading: boolean = useSelector(
    (state: ApplicationState) => state.commands.isLoadingCommands,
  );

  const processWithGroups = (data: any, dataState: State): DataResult => {
    const newDataState = process(data, dataState);

    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };
  const [resultState, setResultState] = React.useState<DataResult>(
    processWithGroups(commandList, dataState),
  );

  const newData = setExpandedState({
    data: resultState.data,
    collapsedIds: collapsedState,
  });

  useEffect(() => {
    setResultState(processWithGroups(commandList, dataState));
  }, [commandList]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(commandList, event.dataState);
    setResultState(newDataState);
    setDataState(event.dataState);
  };

  const handleCloseDialog = () => {
    setCommandHistoryVisible(false);
    clearCommandsByBatchID(dispatch, commandName);
  };

  const DetailsCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    const onClick = () => {
      if (commandName == 'ApplyConfig') {
        getConfigCommandByBatchID(
          dispatch,
          dataItem.Command,
          dataItem.CommandBatchID,
        );
      } else if (commandName == 'ImportItems') {
        getNBCCommandByBatchID(
          dispatch,
          dataItem.Command,
          dataItem.CommandBatchID,
        );
      } else {
        getCommandByBatchID(dispatch, commandName, dataItem.CommandBatchID);
      }
      setCommandHistoryVisible(true);
    };
    return (
      <td>
        <Button
          themeColor={'primary'}
          type="button"
          //disabled={dataItem.Status <= ApiEnums.DeviceStatus.Ok}
          onClick={() => onClick()}
        >
          <StringResourceManager stringKey="VIEW" variant="body1" />
        </Button>
      </td>
    );
  };

  const dateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="CreatedOn"
      />
    );
  };

  const START_DATE_ERRORMSG = GetString('START_DATE_ERRORMSG');
  const END_DATE_ERRORMSG = GetString('END_DATE_ERRORMSG');

  const [startDateRange, setStartDateRange] = React.useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });

  const [endDateRange, setEndDateRange] = React.useState<Date>(new Date());

  const [errorMsg, setErrorMsg] = React.useState(false);

  const [isStartDateRangeChange, setIsStartDateRangeChange] =
    React.useState<boolean>(false);

  const [isEndDateRangeChange, setIsEndDateRangeChange] =
    React.useState<boolean>(false);

  const [useCurrentDateTimeFilter, setUseCurrentDateTimeFilter] =
    React.useState<boolean>(true);

  const handleFilterEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUseCurrentDateTimeFilter(event.target.checked);
    onUseCurrentDateTimeFilterChange?.(event.target.checked);
  };

  const onEndDateRangeChange = (event: any) => {
    setEndDateRange(event.value!);
    setIsEndDateRangeChange(true);
    setErrorMsg(false);
    onStartDateChange?.(event.value!);
  };

  const onStartDateRangeChange = (event: any) => {
    setStartDateRange(event.value!);
    setIsStartDateRangeChange(true);
    setErrorMsg(false);
    onEndDateChange?.(event.value!);
  };

  const dispatchGetCommandByDateRange = () => {
    if (commandName == 'ApplyConfig')
      getAllConfigCommands(
        dispatch,
        startDateRange.toUTCString(),
        useCurrentDateTimeFilter
          ? new Date().toUTCString()
          : endDateRange.toUTCString(),
      );
    else if (commandName == 'ImportItems')
      getAllNBCCommands(
        dispatch,
        startDateRange.toUTCString(),
        useCurrentDateTimeFilter
          ? new Date().toUTCString()
          : endDateRange.toUTCString(),
      );
    else
      getDateCreatedCommandByBatchList(
        dispatch,
        commandName,
        startDateRange.toUTCString(),
        useCurrentDateTimeFilter
          ? new Date().toUTCString()
          : endDateRange.toUTCString(),
      );
  };

  useEffect(() => {
    dispatchGetCommandByDateRange();
  }, []);

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <div className={classes.commandHistoryContainer}>
            <StringResourceManager
              stringKey={title}
              variant="body1"
              style="commandLabel"
              gutterBottom={true}
            />
            <div className={classes.startDateRangeContainer}>
              <div className={classes.labelContainer}>
                <StringResourceManager
                  stringKey={'START_DATE_RANGE_LABEL'}
                  variant="body1"
                  style="dialogLabel"
                  gutterBottom={true}
                />
              </div>
              <div className={classes.startDateTimePickerContainer}>
                <div className={classes.dateTimePickerContainer}>
                  <DateTimePicker
                    value={startDateRange}
                    onChange={onStartDateRangeChange}
                    width={210}
                  />
                  <Button
                    svgIcon={arrowRotateCwIcon}
                    className={classes.button}
                    onClick={dispatchGetCommandByDateRange}
                  >
                    {GetString('REFRESH')}
                  </Button>
                </div>
                {errorMsg && (
                  <Error direction={'start'}>{START_DATE_ERRORMSG}</Error>
                )}
              </div>
            </div>
            <div className={classes.endDateRangeContainer}>
              <div className={classes.labelContainer}>
                <StringResourceManager
                  stringKey={'END_DATE_RANGE_LABEL'}
                  variant="body1"
                  style="dialogLabel"
                  gutterBottom={true}
                />
              </div>
              <div className={classes.endDateTimePickerContainer}>
                <DateTimePicker
                  value={endDateRange}
                  min={startDateRange}
                  onChange={onEndDateRangeChange}
                  disabled={useCurrentDateTimeFilter}
                  width={210}
                />
                {errorMsg && (
                  <Error direction={'start'}>{END_DATE_ERRORMSG}</Error>
                )}
                &nbsp;&nbsp;
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={handleFilterEndDate}
                      name="checkedB"
                      color="primary"
                      checked={useCurrentDateTimeFilter}
                    />
                  }
                  label={GetString('FILTER_END_DATE_TIME')}
                />
              </div>
            </div>
          </div>
          <Grid
            className={classes.gridStyle}
            style={
              commandName == 'ApplyConfig' || commandName == 'ImportItems'
                ? { width: 730, height: '70vh' }
                : { width: 500 }
            }
            data={resultState}
            {...dataState}
            resizable={true}
            sortable={true}
            filterable={true}
            showLoader={loading}
            loader={LoadingPanel}
            pageable={{
              buttonCount: 4,
              pageSizes: [50, 100, 500, 1000, 5000],
            }}
            reorderable={true}
            fixedScroll={false}
            onDataStateChange={dataStateChange}
            size={'small'}
          >
            {commandName == 'ApplyConfig' ? (
              <GridColumn
                field="Command"
                title={GetString('COMMAND')}
                width={'250px'}
              />
            ) : null}
            {commandName == 'ImportItems' ? (
              <GridColumn
                field="Command"
                title={GetString('COMMAND')}
                width={'250px'}
              />
            ) : null}

            <GridColumn
              field="CreatedOn"
              title={GetString('LABEL_DATE')}
              width={'250px'}
              cells={{ data: CreatedOnCell, filterCell: dateFilterCell }}
            />
            <GridColumn
              title={GetString('DETAILS')}
              width={'200px'}
              cells={{ data: DetailsCell }}
              filterable={false}
            />
          </Grid>
          {commandHistoryVisible && (
            <CommandHistoryDialog
              handleCloseDialog={handleCloseDialog}
              command={commandName}
              dropDownFilterCell={dropDownFilterCell}
            />
          )}
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default CommandHistory;
